/**
 * Copy Image and Print PDF in an office's own format: their sheet's cleaned
 * page images, with each blank's value drawn in (layoutFormat.js decides where).
 *
 * Each page is its own canvas — the print path prints one per sheet of paper;
 * the image path stacks them into one picture.
 */
import { canvasToPngBlob } from "../breakdownImage";
import { INK, font, layoutFormat } from "./layoutFormat";

function newCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
}

function draw(ctx, op) {
  ctx.save();
  if (op.type === "text") {
    ctx.translate(op.x, op.y);
    if (op.angle) ctx.rotate(op.angle);
    ctx.font = font(op.size);
    ctx.fillStyle = INK;
    ctx.textBaseline = "alphabetic";
    ctx.fillText(op.text, 0, 0);
  } else if (op.type === "mark") {
    ctx.strokeStyle = INK;
    ctx.lineWidth = op.lineWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    op.points.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.stroke();
  }
  ctx.restore();
}

/** One canvas per page: the sheet, its values, and any note strip below it.
 * `images[i]` is page i's background (an ImageBitmap or a loaded <img>). */
export function paintPages(template, values, images, { createCanvas = newCanvas } = {}) {
  const probe = createCanvas(1, 1).getContext("2d");
  const measure = (text, size) => {
    probe.font = font(size);
    return probe.measureText(String(text)).width;
  };
  const { pages } = layoutFormat(template, values, { measure });
  return pages.map((page, i) => {
    const canvas = createCanvas(page.width, page.height + page.footerHeight);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (!images[i]) throw new Error(`page ${i + 1} image missing`);
    ctx.drawImage(images[i], 0, 0, page.width, page.height);
    if (page.footerHeight) {
      ctx.strokeStyle = "#c9ced6";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(page.width * 0.06, page.height + 0.5);
      ctx.lineTo(page.width * 0.94, page.height + 0.5);
      ctx.stroke();
    }
    page.ops.forEach((op) => draw(ctx, op));
    return canvas;
  });
}

/** The pages stacked into one canvas (one page is returned as is). */
export function stackPages(canvases, { createCanvas = newCanvas } = {}) {
  if (canvases.length === 1) return canvases[0];
  const width = Math.max(...canvases.map((c) => c.width));
  const height = canvases.reduce((sum, c) => sum + c.height, 0);
  const out = createCanvas(width, height);
  const ctx = out.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  let y = 0;
  canvases.forEach((c) => {
    ctx.drawImage(c, 0, y);
    y += c.height;
  });
  return out;
}

/** Copy Image: the filled-in sheet as one PNG. */
export async function paintFormatImage(template, values, images) {
  return canvasToPngBlob(stackPages(paintPages(template, values, images)));
}
